import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { cn } from '@/lib/utils';
import BrandMark from './BrandMark';
import MenuContent from './MenuContent';
import { NavModel } from './nav-model';

const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Desktop sidebar: a floating, rounded glass card inset 8px from the
 * viewport edge. The logo sits centred above the card, on the backdrop,
 * level with the top bar; the collapse control is pinned to the end of
 * that row. Collapsed, the card narrows to an icon rail and the logo mark
 * turns into the expand button on hover / keyboard focus.
 */
export default function SideMenu({
    model,
    collapsed,
    canToggle,
    onToggleCollapsed,
    logoUrl,
    systemName,
}: {
    model: NavModel;
    collapsed: boolean;
    /** False on tablets, where the rail is forced (see useSidebarCollapsed). */
    canToggle: boolean;
    onToggleCollapsed: () => void;
    logoUrl?: string | null;
    systemName?: string | null;
}) {
    const { t } = useTranslation('crm');

    let logoRow: React.ReactNode;
    if (!collapsed) {
        logoRow = (
            <>
                <Link
                    to="/"
                    aria-label={t('chrome.goToDashboard')}
                    className={cn('flex items-center rounded-md no-underline', focusRing)}
                >
                    <BrandMark logoUrl={logoUrl} name={systemName} />
                </Link>
                {canToggle && (
                    <button
                        type="button"
                        onClick={onToggleCollapsed}
                        aria-label={t('chrome.collapseSidebar')}
                        aria-expanded
                        title={t('chrome.collapseSidebar')}
                        className={cn(
                            'absolute end-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-glass-nav hover:text-foreground',
                            focusRing
                        )}
                    >
                        {/* Panel icons point left; mirrored in RTL. */}
                        <PanelLeftClose aria-hidden="true" className="size-4 rtl:-scale-x-100" />
                    </button>
                )}
            </>
        );
    } else if (canToggle) {
        logoRow = (
            <button
                type="button"
                onClick={onToggleCollapsed}
                aria-label={t('chrome.expandSidebar')}
                aria-expanded={false}
                title={t('chrome.expandSidebar')}
                className={cn(
                    'group flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-glass-nav hover:text-foreground',
                    focusRing
                )}
            >
                <span className="group-hover:hidden group-focus-visible:hidden">
                    <BrandMark logoUrl={logoUrl} name={systemName} compact />
                </span>
                <PanelLeftOpen
                    aria-hidden="true"
                    className="hidden size-4 group-hover:block group-focus-visible:block rtl:-scale-x-100"
                />
            </button>
        );
    } else {
        logoRow = (
            <Link
                to="/"
                aria-label={t('chrome.goToDashboard')}
                className={cn('flex items-center rounded-md no-underline', focusRing)}
            >
                <BrandMark logoUrl={logoUrl} name={systemName} compact />
            </Link>
        );
    }

    return (
        <div
            className={cn(
                'hidden h-full shrink-0 flex-col ps-2 transition-[width] duration-200 ease-out md:flex',
                collapsed ? 'w-[var(--sidebar-w-collapsed)]' : 'w-[var(--sidebar-w)]'
            )}
        >
            {/* Same height as the top bar, so logo and search line up. */}
            <div className="relative flex h-14 shrink-0 items-center justify-center px-2">
                {logoRow}
            </div>
            <aside
                aria-label={t('chrome.mainNavigation', 'Main navigation')}
                className="mb-2 min-h-0 flex-1 overflow-hidden rounded-[calc(var(--radius)+6px)] border border-glass-nav-border bg-glass-nav backdrop-blur-xl backdrop-saturate-150"
            >
                <MenuContent model={model} collapsed={collapsed} />
            </aside>
        </div>
    );
}
